import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Target, ShieldCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api, { employeeLicensesAPI } from '../lib/api';

// v4.33.4 — Firearms Qualification records. Populated when a supervisor verifies
// an Armed holder's last firearms qualification on an evaluation (by uploaded
// card or against the range roster). Read-only here; the attached file is
// access-controlled + audited server-side.
export function FirearmsQualificationPanel({ employeeId }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await employeeLicensesAPI.firearmsQualifications(employeeId);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);

  const viewFile = async (q) => {
    try {
      const res = await api.get(employeeLicensesAPI.firearmsQualificationFileUrl(employeeId, q.id), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? 'You do not have access to this credential file.'
        : 'Could not open the credential file.');
    }
  };

  // Hide the panel entirely when there is nothing to show (keeps non-armed
  // employee records clean).
  if (loaded && rows.length === 0) return null;

  return (
    <Card data-testid="firearms-qualification-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-slate-700" /> Firearms Qualification
        </CardTitle>
        <CardDescription>The employee's last firearms qualification, verified from evaluations (by card or range roster).</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((q) => (
              <TableRow key={q.id} data-testid={`firearms-qual-row-${q.id}`}>
                <TableCell className="font-mono">{q.qualification_date}</TableCell>
                <TableCell>
                  <Badge className="bg-emerald-100 text-emerald-800">{q.result || 'Pass'}</Badge>
                </TableCell>
                <TableCell>{q.score || '—'}</TableCell>
                <TableCell className="text-xs text-slate-500">
                  {q.credential_source && (
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
                      <span className="truncate max-w-[220px]" title={q.credential_source}>{q.credential_source}</span>
                      {q.can_view_credential_file && (
                        <button type="button" onClick={() => viewFile(q)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline shrink-0"
                                data-testid={`firearms-qual-view-${q.id}`}>
                          <FileText className="w-3 h-3" /> View
                        </button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default FirearmsQualificationPanel;
